import {
  DayPickerStrings,
  FlexSpinner,
  FormikGraphQLDatePicker,
  onFormatDate,
} from 'components'
import { FormikDropdown, FormikSpinButton } from 'formik-office-ui-fabric-react'
import { FieldStack, FieldStackItem, FormStack } from 'forms/FormStack'
import { useCurrentFormik } from 'hooks/useCurrentFormikContext'
import { useCurrentProving } from 'hooks/useCurrentProving'
import {
  DayOfWeek,
  IDropdownOption,
  IStackTokens,
  Label,
  Stack,
} from 'office-ui-fabric-react'
import { Position } from 'office-ui-fabric-react/lib/utilities/positioning'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'theme'
import {
  useProvingsQuery,
  useResourceTypesQuery,
} from 'views/ProvingDetailsView/hooks'
import { Section } from 'views/ProvingDetailsView/Layout'
import {
  ResourceSearchFreeResourceList,
  ResourceSearchOccupiedResourcesList,
} from 'views/ProvingDetailsView/lists'

export const ResourceSearchForm: React.FC = () => {
  const allResourceBookings = useProvingsQuery()
  const { t } = useTranslation()
  const [proving] = useCurrentProving()
  const theme = useTheme()
  const [type, setType] = React.useState()

  const resourceRequirementTypeOptions: IDropdownOption[] = [
    { key: 'workstation', text: t('dropdown.option.rrWorkstation') },
    { key: 'liftingPlatform', text: t('dropdown.option.rrLiftingPlatform') },
    {
      key: 'groundWorkStation',
      text: t('dropdown.option.rrGroundWorkStation'),
    },
    { key: 'coldCell', text: t('dropdown.option.rrColdCell') },
    { key: 'testTrack', text: t('dropdown.option.rrTestTrack') },
    { key: 'parkingSpace', text: t('dropdown.option.rrParkingSpace') },
    { key: 'accessCard', text: t('dropdown.option.rrAccessCard') },
    { key: 'fuelCard', text: t('dropdown.option.rrFuelCard') },
    { key: 'radio', text: t('dropdown.option.rrRadio') },
  ]

  const formikContext = useCurrentFormik()
  const initialValues = formikContext ? formikContext.initialValues : undefined
  const resourceType = initialValues.resourceRequirementType
    ? initialValues.resourceRequirementType
    : ''

  const resourceBookings = () => {
    let occupiedRessourcesItems: any[] = []

    if (
      allResourceBookings &&
      allResourceBookings.data &&
      allResourceBookings.data.provings
    ) {
      allResourceBookings.data.provings.forEach((p: any) => {
        if (p.provingGround!._id === proving.provingGround!._id) {
          p.resourceBookings!.forEach((rb: any) => {
            if (
              (rb.resource && rb.resource.type === resourceType) ||
              (rb.resource && rb.resource.category === resourceType)
            ) {
              if (
                rb.dateFrom < initialValues.dateTo &&
                initialValues.dateFrom < rb.dateTo
              ) {
                ;(rb as any).provingId = p._id
                occupiedRessourcesItems.push(rb)
              }
            }
          })
        }
      })
    }
    occupiedRessourcesItems = occupiedRessourcesItems.sort((a, b) =>
      a.resource.title.localeCompare(b.resource.title, 'de', {
        numeric: true,
      })
    )
    return occupiedRessourcesItems
  }

  const getResourceParent = () => {
    let currentType: any = resourceType
    switch (currentType) {
      case 'workstation':
        currentType = 'officeRoom'
        break
      default:
        break
    }
    return currentType
  }

  const parentType = getResourceParent()
  const occupiedRessources = resourceBookings()
  const resources = useResourceTypesQuery({
    variables: {
      provingGroundId: proving!.provingGround!._id,
      type: parentType,
      category: ' ',
    },
  })

  const listItemsByType = useResourceTypesQuery({
    variables: {
      provingGroundId: proving!.provingGround!._id,
      type: resourceType,
      category: ' ',
    },
  })

  const listItemsByCategory = useResourceTypesQuery({
    variables: {
      provingGroundId: proving!.provingGround!._id,
      category: resourceType,
      type: ' ',
    },
  })

  const freeResources = () => {
    let freeResourcesItems: any[] = []
    if (listItemsByType.data!.resources) {
      listItemsByType.data!.resources.forEach((res) => {
        freeResourcesItems.push(res)
      })
    }

    if (listItemsByCategory.data!.resources) {
      listItemsByCategory.data!.resources.forEach((res) => {
        freeResourcesItems.push(res)
      })
    }

    if (occupiedRessources) {
      occupiedRessources.forEach((or) => {
        freeResourcesItems.forEach((fri) => {
          const index = fri._id.indexOf(or.resource._id, 0)
          if (index > -1) {
            const newIndex = freeResourcesItems.indexOf(fri, 0)

            freeResourcesItems.splice(newIndex, 1)
          }
        })
      })
    }

    if (parentType === 'officeRoom') {
      freeResourcesItems = freeResourcesItems.filter((f) => {
        return f.parentId === type
      })
    }
    freeResourcesItems = freeResourcesItems.sort((a, b) =>
      a.title.localeCompare(b.title, 'de', {
        numeric: true,
      })
    )
    return freeResourcesItems
  }

  const typeOptions = resources.data!.resources
    ? [
        ...resources.data!.resources.map(
          (item: any) =>
            ({
              key: item._id,
              text: item.title,
            } as IDropdownOption)
        ),
      ]
    : []

  const stackTokens: IStackTokens = {
    childrenGap: 5,
    padding: 10,
  }

  return (
    <FlexSpinner
      loading={listItemsByType.loading}
      error={listItemsByType.error}
    >
      <FormStack>
        <Stack>
          <Label>{t('Ressourcensuche')}</Label>
          <FieldStack>
            <FieldStackItem
              name={'resourceRequirementType'}
              render={(fieldProps) => (
                <FormikDropdown
                  label={'Suche nach: '}
                  {...fieldProps}
                  options={resourceRequirementTypeOptions}
                  disabled={true}
                />
              )}
            />
          </FieldStack>
          {parentType === 'officeRoom' && (
            <FieldStack>
              <FieldStackItem
                name={'type'}
                render={(fieldProps) => (
                  <FormikDropdown
                    label={'Suche nach: '}
                    {...fieldProps}
                    options={typeOptions}
                    onChanged={(option) => {
                      if (option.key) {
                        setType(option.key)
                        freeResources()
                      }
                    }}
                  />
                )}
              />
            </FieldStack>
          )}
          <FieldStack horizontal={true}>
            <FieldStackItem
              name={'dateFrom'}
              render={(fieldProps) => (
                <FormikGraphQLDatePicker
                  label={t('field.dateFrom.label')}
                  defaultValue={proving.dateFrom}
                  // minDate={new Date(proving.dateFrom)}
                  // maxDate={new Date(proving.dateTo)}
                  firstDayOfWeek={DayOfWeek.Monday}
                  strings={DayPickerStrings}
                  showWeekNumbers={true}
                  formatDate={onFormatDate as any}
                  {...fieldProps}
                  initialPickerDate={new Date(proving.dateFrom)}
                />
              )}
            />
            <FieldStackItem
              name={'dateTo'}
              render={(fieldProps) => (
                <FormikGraphQLDatePicker
                  label={t('field.dateTo.label')}
                  defaultValue={proving.dateTo}
                  // minDate={new Date(proving.dateFrom)}
                  // maxDate={new Date(proving.dateTo)}
                  firstDayOfWeek={DayOfWeek.Monday}
                  strings={DayPickerStrings}
                  showWeekNumbers={true}
                  formatDate={onFormatDate as any}
                  {...fieldProps}
                  initialPickerDate={new Date(proving.dateFrom)}
                />
              )}
            />
          </FieldStack>
          <FieldStackItem
            name={'amount'}
            render={(fieldProps) => (
              <FormikSpinButton
                label={t('field.amountNeeded.label')}
                labelPosition={Position.top}
                min={1}
                max={30}
                step={1}
                {...fieldProps}
                disabled={true}
              />
            )}
          />
          <Stack horizontal={true} tokens={stackTokens}>
            <Stack.Item grow={1}>
              <Section
                label={t('field.freeResources.label')}
                backgroundColor={theme.palette.greenLight}
              >
                <FieldStackItem
                  name={'freeResources'}
                  render={(fieldProps) => (
                    <ResourceSearchFreeResourceList
                      resources={freeResources()}
                      fieldProps={fieldProps}
                      initialValues={initialValues}
                      {...fieldProps}
                    />
                  )}
                />
              </Section>
            </Stack.Item>
            <Stack.Item grow={1}>
              <Section
                label={t('field.occupiedResources.label')}
                backgroundColor={theme.palette.themeLight}
              >
                <FieldStackItem
                  name={'occupiedResources'}
                  render={(fieldProps) => (
                    <ResourceSearchOccupiedResourcesList
                      allResourceBookings={allResourceBookings}
                      fieldProps={fieldProps}
                      {...fieldProps}
                    />
                  )}
                />
              </Section>
            </Stack.Item>
          </Stack>
        </Stack>
      </FormStack>
    </FlexSpinner>
  )
}
